import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import I18n from '@/modules/i18n';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { useModalManager } from '@/components/common/Modal';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import { IStudent, ISubmission } from '@/components/staffer/Grading/types';

interface AssessmentFlashMessageProps {
  student: IStudent;
  submission: ISubmission
}
const AssessmentFlashMessage = ({ student, submission }: AssessmentFlashMessageProps) => {
  const {
    assignment,
    isAssessment,
    programId,
  } = useGradingStore();
  const client = useQueryClient();
  const modalManager = useModalManager();

  const searchParams = new URLSearchParams(window.location.search);
  const classroomId = searchParams.get('classroom_id');
  const lessonId = searchParams.get('lesson_id');

  const submitMutation = useMutation({
    mutationFn: () => Axios.put(Routes.shared_program_lesson_assessment_path(
      programId,
      lessonId,
      assignment.assignment_details?.exercise_id,
      { classroom_id: classroomId, no_flash: true, student_id: student.id },
    )),
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.log(err);
    },
    onSuccess: async () => {
      // For Grade by Student
      await client.invalidateQueries(['submission', assignment.id.toString(), student.id.toString()]);
      // For Grade by Question
      await client.invalidateQueries(['Submissions', assignment.id.toString()]);
    },
  });

  useEffect(() => {
    submitMutation.reset();
  }, [submission?.id, student.id]);

  if (!isAssessment() || submission?.submitted_at != null) return null;

  const dueDate = moment.tz(assignment.due_datetime, 'UTC');
  const now = moment.tz('UTC');
  const studentName = `${student?.first_name} ${student?.last_name}`;

  if (now.isAfter(dueDate)) {
    return (
      <div className={`
        tw-flex tw-items-center tw-text-red tw-bg-[#fdf2f2] tw-py-1.5 tw-px-2
        tw-border tw-border-solid tw-border-red tw-rounded-md
      `}
      >
        <Icon
          className="tw-mr-2"
          color="tci-red"
          icon="Warning"
          size="24"
        />
        <Text
          className="tw-mr-2"
          size="sm"
        >
          {I18n.t('assessment_grading_blocked_past_due')}
        </Text>
        <button
          className="btn btn--purple btn--sm"
          type="button"
          onClick={modalManager.open}
        >
          {I18n.t('turn_in')}
        </button>
        <SubmitModal
          close={modalManager.close}
          headerText={I18n.t('turn_in')}
          isOpen={modalManager.isOpen}
          onSubmit={modalManager.close}
        >
          <Text>
            {I18n.t('assessment_turn_in_confirmation', { student: studentName })}
          </Text>

          <SubmitModal.SubmitButton
            disabled={submitMutation.isLoading}
            onClick={submitMutation.mutate}
          >
            {I18n.t('submit')}
          </SubmitModal.SubmitButton>
        </SubmitModal>
      </div>
    );
  }

  return (
    <Text
      className={`
        tw-flex tw-items-center !tw-text-[#933615] tw-bg-[#fef7ee] tw-py-1.5 tw-px-2
        tw-border tw-border-solid tw-border-[#f69c05] tw-rounded-md
      `}
      size="sm"
    >
      {I18n.t('assessment_grading_blocked_in_progresss')}
    </Text>
  );
};

export default AssessmentFlashMessage;
